import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { getPostAuthPath, storePendingInvitation, getPendingInvitation } from '../utils/userCompany';
import axios from '../config/axios';
import './Login.css';
import Modal from '../components/Modal';

const LoginContent = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const { login, loginWithGoogle, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    const invitation = searchParams.get('invitation');
    if (invitation) {
      storePendingInvitation(invitation);
    }
  }, [searchParams]);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setError('');
      const result = await loginWithGoogle(credentialResponse.credential, getPendingInvitation());
      if (result.success) {
        navigate(getPostAuthPath(result.user));
      } else {
        setError(result.error);
      }
    } catch (googleError) {
      console.error('Google login error:', googleError);
      setError(t('googleLoginFailed'));
    }
  };

  const handleGoogleError = () => {
    setError(t('googleLoginFailed'));
  };

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const result = await login(email, password);
    if (result.success) {
      navigate(getPostAuthPath(result.user));
    } else {
      setError(result.error);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    try {
      const response = await axios.post('/api/auth/forgot-password', {
        email: forgotPasswordEmail
      });
      if (response.data.success) {
        setForgotPasswordSuccess(response.data.message);
        setForgotPasswordEmail('');
      }
    } catch (forgotError) {
      setForgotPasswordError(formatError(forgotError) || t('resetEmailFailed'));
    }
  };

  const logoPath = '/service_in_mind_logo_dark.png';

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo-container">
          <img
            src={logoPath}
            alt="Service in Mind"
            className="login-logo"
          />
        </div>
        <h2>{t('loginTitle')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <div className="error">{error}</div>}
          <button type="submit" className="btn btn-primary">
            {t('login')}
          </button>
        </form>
        {googleClientId && (
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '10px', color: '#666' }}>{t('or')}</div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
              />
            </div>
          </div>
        )}
        <p className="forgot-password-link">
          <button
            type="button"
            className="login-forgot-link"
            onClick={() => setShowForgotPassword(true)}
          >
            {t('forgotPassword')}
          </button>
        </p>
        <p className="register-link">
          {t('noAccount')}{' '}
          <a href="/register">{t('registerHere')}</a>
        </p>
      </div>

      <Modal
        isOpen={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          setForgotPasswordEmail('');
          setForgotPasswordError('');
          setForgotPasswordSuccess('');
        }}
        title={t('forgotPasswordTitle')}
      >
        <form onSubmit={handleForgotPassword}>
          <div className="form-group">
            <label>{t('email')}</label>
            <input
              type="email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              required
            />
          </div>
          {forgotPasswordError && <div className="error">{forgotPasswordError}</div>}
          {forgotPasswordSuccess && <div className="success">{forgotPasswordSuccess}</div>}
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary">
              {t('sendResetEmail')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail('');
                setForgotPasswordError('');
                setForgotPasswordSuccess('');
              }}
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

const Login = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        <LoginContent />
      </GoogleOAuthProvider>
    );
  }

  return <LoginContent />;
};

export default Login;
